
import { createContext, useContext, ReactNode } from "react";
import { AuthContextType } from "./types";
import { useAuthState } from "./hooks/useAuthState";
import { useLogin } from "./hooks/useLogin";
import { useLogout } from "./hooks/useLogout";
import { useRegister } from "./hooks/useRegister";
import { useProfileManagement } from "./hooks/useProfileManagement";
import { usePasswordManagement } from "./hooks/usePasswordManagement";
import { useInvitation } from "./hooks/useInvitation";

// Create the context
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: false,
  login: async () => false,
  loginWithOTP: async () => false,
  logout: async () => {},
  register: async () => false,
  updateProfile: async () => false,
  resetPassword: async () => false,
  updatePassword: async () => false,
  requestInvitation: async () => false,
  authError: null,
  setAuthError: () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { 
    user, 
    setUser, 
    isLoading, 
    setIsLoading, 
    authError, 
    setAuthError 
  } = useAuthState();
  
  const { login, loginWithOTP } = useLogin(setIsLoading, setAuthError);
  const { logout } = useLogout(setIsLoading, setUser);
  const { register } = useRegister(setIsLoading, setAuthError);
  const { updateProfile } = useProfileManagement(user, setUser, setIsLoading);
  const { resetPassword, updatePassword } = usePasswordManagement(setIsLoading, setAuthError);
  const { requestInvitation } = useInvitation(setIsLoading, setAuthError);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        authError,
        setAuthError,
        login,
        loginWithOTP,
        logout,
        register,
        updateProfile,
        resetPassword,
        updatePassword,
        requestInvitation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Export the types
export type { User, AuthContextType } from './types';
